import jsPDF from "jspdf";
import { AMBIENTE_PRODUCAO, LOGO_RELATORIO_PADRAO, NOME_PLATAFORMA } from "../Config/branding";
import macrofluxoSgq from "./macrofluxo-sgq.jpg";

const azul = [16, 76, 111];
const verde = [47, 99, 34];
const cinzaClaro = [238, 242, 245];

function valor(conteudo) {
  return String(conteudo ?? "").trim() || "-";
}

function formatarData(data) {
  const [ano, mes, dia] = String(data || "").split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : valor(data);
}

function formatarRevisao(revisao) {
  const numero = String(revisao ?? "").trim();
  return numero ? numero.padStart(2, "0") : "";
}

export function nomePdfManualQualidade(registro) {
  return `MQ-01-MANUAL-DA-QUALIDADE-REV-${formatarRevisao(registro?.revisao)}.pdf`;
}

function carregarImagem(url) {
  return new Promise((resolve, reject) => {
    const imagem = new Image();
    imagem.crossOrigin = "anonymous";
    imagem.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = imagem.naturalWidth;
        canvas.height = imagem.naturalHeight;
        canvas.getContext("2d").drawImage(imagem, 0, 0);
        resolve({ dataUrl: canvas.toDataURL("image/png"), largura: imagem.naturalWidth, altura: imagem.naturalHeight });
      } catch (error) {
        reject(error);
      }
    };
    imagem.onerror = reject;
    imagem.src = url;
  });
}

async function carregarLogoRelatorio() {
  const tenantId = String(localStorage.getItem("tenantId") || "").trim();
  const logoConfigurada = localStorage.getItem(`logoSistemaUrl:${tenantId}`) || localStorage.getItem("logoRelatorioUrl") || LOGO_RELATORIO_PADRAO;
  try {
    return await carregarImagem(logoConfigurada);
  } catch (error) {
    if (logoConfigurada === LOGO_RELATORIO_PADRAO) return null;
    try {
      return await carregarImagem(LOGO_RELATORIO_PADRAO);
    } catch (erroLogoPadrao) {
      return null;
    }
  }
}

export async function gerarPdfManualQualidade(registro) {
  const [logo, macrofluxo] = await Promise.all([
    carregarLogoRelatorio(),
    carregarImagem(macrofluxoSgq).catch(() => null),
  ]);
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const margem = 18;
  const largura = 210 - margem * 2;
  const limiteInferior = 279;
  let y = 36;

  function cabecalho() {
    const topo = 8;
    const altura = 20;
    pdf.setDrawColor(90, 90, 90);
    pdf.setLineWidth(0.25);
    pdf.rect(margem, topo, largura, altura);
    pdf.line(margem + 58, topo, margem + 58, topo + altura);
    pdf.line(168, topo, 168, topo + altura);
    pdf.line(margem + 58, topo + 11, 168, topo + 11);

    if (logo) {
      const larguraMaxima = 50;
      const alturaMaxima = 15;
      const proporcao = Math.min(larguraMaxima / logo.largura, alturaMaxima / logo.altura);
      const larguraLogo = logo.largura * proporcao;
      const alturaLogo = logo.altura * proporcao;
      pdf.addImage(logo.dataUrl, "PNG", margem + (58 - larguraLogo) / 2, topo + (altura - alturaLogo) / 2, larguraLogo, alturaLogo);
    }

    pdf.setTextColor(15, 15, 15);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text("MANUAL DA QUALIDADE", 113, topo + 7, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 116, 173);
    pdf.setFontSize(7.5);
    pdf.text("Sistema de Gestão da Qualidade", 113, topo + 17, { align: "center" });

    pdf.setTextColor(15, 15, 15);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10.5);
    pdf.text(valor(registro.codigoDocumento), 180, topo + 5, { align: "center" });
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.text(`Versão ${formatarRevisao(registro.revisao)}`, 180, topo + 10, { align: "center" });
    pdf.text("Aprovado em", 180, topo + 14, { align: "center" });
    pdf.text(formatarData(registro.dataAprovacao), 180, topo + 18, { align: "center" });
  }

  function novaPagina() {
    pdf.addPage();
    cabecalho();
    y = 36;
  }

  function garantirEspaco(altura) {
    if (y + altura > limiteInferior) novaPagina();
  }

  function tituloSecao(titulo) {
    garantirEspaco(11);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(14);
    pdf.setTextColor(...azul);
    pdf.text(titulo, margem, y);
    pdf.setTextColor(20, 20, 20);
    y += 8;
  }

  function subtitulo(titulo) {
    garantirEspaco(8);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(20, 20, 20);
    pdf.text(titulo, margem, y);
    y += 5;
  }

  function texto(conteudo, opcoes = {}) {
    const { cor = [20, 20, 20], tamanho = 9, negrito = false, alinhamento = "left", recuo = 0 } = opcoes;
    const linhasFonte = valor(conteudo).split("\n");
    pdf.setFont("helvetica", negrito ? "bold" : "normal");
    pdf.setFontSize(tamanho);
    pdf.setTextColor(...cor);
    linhasFonte.forEach((linhaFonte) => {
      if (!linhaFonte.trim()) {
        y += 2.5;
        return;
      }
      const bullet = /^-\s+/.test(linhaFonte);
      const conteudoLinha = bullet ? linhaFonte.replace(/^-\s+/, "") : linhaFonte;
      const x = margem + recuo + (bullet ? 6 : 0);
      const larguraTexto = largura - recuo - (bullet ? 6 : 0);
      const linhas = pdf.splitTextToSize(conteudoLinha, larguraTexto);
      linhas.forEach((linha, indice) => {
        garantirEspaco(4.5);
        if (bullet && indice === 0) pdf.text("•", margem + recuo + 2, y);
        pdf.text(linha, alinhamento === "center" ? 105 : x, y, alinhamento === "center" ? { align: "center" } : undefined);
        y += 4.2;
      });
      y += 0.8;
    });
    y += 1;
  }

  function destaque(conteudo, subtituloDestaque = "") {
    const linhas = pdf.splitTextToSize(valor(conteudo), largura - 12);
    const altura = Math.max(15, linhas.length * 5 + (subtituloDestaque ? 7 : 4));
    garantirEspaco(altura + 3);
    pdf.setFillColor(...cinzaClaro);
    pdf.rect(margem, y, largura, altura, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.setTextColor(5, 58, 102);
    linhas.forEach((linha, indice) => pdf.text(linha, 105, y + 6 + indice * 5, { align: "center" }));
    if (subtituloDestaque) {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.text(subtituloDestaque, 105, y + altura - 3, { align: "center" });
    }
    y += altura + 4;
  }

  function inserirMacrofluxo() {
    if (!macrofluxo) return;
    const altura = largura * (macrofluxo.altura / macrofluxo.largura);
    garantirEspaco(altura + 9);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(20, 20, 20);
    pdf.text("Macrofluxo dos processos", margem, y);
    y += 4;
    pdf.addImage(macrofluxo.dataUrl, "PNG", margem, y, largura, altura);
    y += altura + 5;
  }

  function tabelaMatriz(conteudo) {
    const linhasTabela = String(conteudo || "").split("\n").map((linha) => linha.split("|")).filter((colunas) => colunas.length >= 2);
    const larguraClausula = 46;
    const larguraDocumento = largura - larguraClausula;

    function cabecalhoTabela() {
      garantirEspaco(10);
      pdf.setFillColor(...cinzaClaro);
      pdf.setDrawColor(40, 40, 40);
      pdf.rect(margem, y, larguraClausula, 9, "FD");
      pdf.rect(margem + larguraClausula, y, larguraDocumento, 9, "FD");
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      pdf.setTextColor(20, 20, 20);
      pdf.text("Cláusula ISO 9001", margem + larguraClausula / 2, y + 5.8, { align: "center" });
      pdf.text("Documento(s) Internos que Atendem", margem + larguraClausula + larguraDocumento / 2, y + 5.8, { align: "center" });
      y += 9;
    }

    cabecalhoTabela();
    linhasTabela.forEach(([clausula, ...documentos]) => {
      const textoDocumentos = documentos.join("|").trim();
      const linhasClausula = pdf.splitTextToSize(clausula.trim(), larguraClausula - 4);
      const linhasDocumento = pdf.splitTextToSize(textoDocumentos, larguraDocumento - 4);
      const alturaLinha = Math.max(linhasClausula.length, linhasDocumento.length) * 4 + 3;
      if (y + alturaLinha > limiteInferior) {
        novaPagina();
        cabecalhoTabela();
      }
      pdf.setDrawColor(40, 40, 40);
      pdf.rect(margem, y, larguraClausula, alturaLinha);
      pdf.rect(margem + larguraClausula, y, larguraDocumento, alturaLinha);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.5);
      pdf.setTextColor(20, 20, 20);
      pdf.text(linhasClausula, margem + 2, y + 4.5);
      pdf.text(linhasDocumento, margem + larguraClausula + 2, y + 4.5);
      y += alturaLinha;
    });
    y += 5;
  }

  function quadroAprovacao() {
    garantirEspaco(30);
    const col1 = 48;
    const col2 = 49;
    const col3 = largura - col1 - col2;
    const altura = 23;
    pdf.setDrawColor(40, 40, 40);
    pdf.rect(margem, y, col1, altura);
    pdf.rect(margem + col1, y, col2, altura);
    pdf.rect(margem + col1 + col2, y, col3, altura);
    pdf.line(margem, y + 11.5, margem + col1, y + 11.5);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.5);
    pdf.text("Data da Aprovação:", margem + 2, y + 4);
    pdf.setFont("helvetica", "bold");
    pdf.text(formatarData(registro.dataAprovacao), margem + 2, y + 8.5);
    pdf.setFont("helvetica", "normal");
    pdf.text("Data Próxima revisão:", margem + 2, y + 15.5);
    pdf.setFont("helvetica", "bold");
    pdf.text(formatarData(registro.dataProximaRevisao), margem + 2, y + 20);
    pdf.setFont("helvetica", "normal");
    pdf.text("Aprovado por:", margem + col1 + 2, y + 4);
    pdf.setFont("helvetica", "bold");
    pdf.text(valor(registro.aprovadoPor), margem + col1 + 2, y + 9);
    pdf.setFont("helvetica", "normal");
    pdf.text("Última Alteração:", margem + col1 + col2 + 2, y + 4);
    pdf.setFontSize(7);
    const alteracoes = pdf.splitTextToSize(valor(registro.historicoAlteracoes), col3 - 4);
    pdf.text(alteracoes, margem + col1 + col2 + 2, y + 8.5);
    y += altura + 3;
  }

  cabecalho();
  tituloSecao("1. APRESENTAÇÃO");
  texto(registro.apresentacao);
  tituloSecao("2. POLÍTICA DA QUALIDADE");
  destaque(`“${valor(registro.politicaQualidade)}”`, valor(registro.politicaRevisao));
  tituloSecao("3. MISSÃO, VISÃO E VALORES");
  subtitulo("Missão");
  texto(registro.missao);
  subtitulo("Visão");
  texto(registro.visao);
  subtitulo("Valores");
  texto(registro.valores);
  tituloSecao("4. ESCOPO DO SGQ");
  texto(registro.escopo, { cor: verde, tamanho: 10.5, negrito: true, alinhamento: "center" });
  texto(`(${valor(registro.complementoEscopo)})`, { tamanho: 9, alinhamento: "center" });
  texto(`- CERTIFICADO Nº ${valor(registro.certificadoNumero)}\n- Válido até: ${formatarData(registro.certificadoValidade)}`);

  tituloSecao("5. ATENDIMENTO AOS REQUISITOS DA ISO 9001:2015");
  tituloSecao("5.1 Contexto da Organização");
  subtitulo("4.1 Entendimento da Organização e seu Contexto - Mudanças Climáticas");
  texto(registro.mudancasClimaticas);
  texto(registro.contextoOrganizacao);
  texto(registro.partesInteressadas);
  texto(registro.escopoRequisito);
  subtitulo("4.4 Processos do SGQ");
  texto(registro.processosSGQ);
  inserirMacrofluxo();

  tituloSecao("5.2 - Liderança");
  texto(registro.lideranca);
  tituloSecao("5.3 Planejamento");
  texto(registro.planejamento);
  tituloSecao("5.4 Cláusula 7 - Apoio");
  texto(registro.apoio);
  tituloSecao("5.5 Cláusula 8 - Operação");
  texto(registro.operacao);
  tituloSecao("5.6 Cláusula 9 - Avaliação de Desempenho");
  texto(registro.avaliacaoDesempenho);
  tituloSecao("5.7 Cláusula 10 - Melhoria");
  texto(registro.melhoria);
  tituloSecao("6. ESTRUTURA ORGANIZACIONAL");
  texto(registro.estruturaOrganizacional);
  tituloSecao("7. MATRIZ DE RELAÇÃO ISO 9001 - DOCUMENTOS DO SGQ");
  tabelaMatriz(registro.matrizRelacao);
  quadroAprovacao();

  const totalPaginas = pdf.getNumberOfPages();
  for (let pagina = 1; pagina <= totalPaginas; pagina += 1) {
    pdf.setPage(pagina);
    pdf.setDrawColor(40, 40, 40);
    pdf.line(margem, 286, 210 - margem, 286);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(6.5);
    pdf.setTextColor(...verde);
    const identificacaoEmpresa = AMBIENTE_PRODUCAO ? ` ${NOME_PLATAFORMA.toUpperCase()}` : "";
    pdf.text(`Manual da Qualidade - Sistema de Gestão da Qualidade${identificacaoEmpresa}`, margem, 291);
    pdf.text(`Pág. ${pagina} de ${totalPaginas}`, 210 - margem, 291, { align: "right" });
  }

  return pdf;
}
